import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Phone, Mail, MessageCircle, MapPin, Star, Quote,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import DiagnosticWizard from "@/components/DiagnosticWizard";
import PacksSection from "@/components/PacksSection";
import ContactForm from "@/components/ContactForm";
import {
  BENEFITS, SERVICES, STEPS, TESTIMONIALS, FAQ, TRUST, IMAGES, CONTACT, PRESTATIONS_NOTE,
} from "@/data/content";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`py-20 lg:py-28 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const SectionHeader = ({ eyebrow, title, subtitle, center = true }) => (
  <div className={`mb-14 ${center ? "text-center max-w-3xl mx-auto" : "max-w-2xl"}`}>
    {eyebrow && <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">{eyebrow}</p>}
    <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{title}</h2>
    {subtitle && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{subtitle}</p>}
  </div>
);

export default function Home() {
  const navigate = useNavigate();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: `url(${IMAGES.heroAbstract})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <span className="inline-flex items-center gap-2 rounded-full bg-green-50 text-green-700 px-4 py-1.5 text-sm font-semibold mb-6">
                <MapPin size={15} /> Spécialiste de la visibilité locale
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1]">
                Aidez votre commerce à attirer plus de <span className="text-green-600">clients locaux</span>.
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                Nous améliorons votre visibilité en ligne avec une méthode simple, rapide et adaptée aux commerces de proximité.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  data-testid="hero-primary-cta"
                  onClick={() => navigate("/diagnostic")}
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-7 py-4 font-semibold text-lg transition-colors shadow-sm"
                >
                  Faire le diagnostic gratuit <ArrowRight size={20} />
                </button>
                <button
                  data-testid="hero-secondary-cta"
                  onClick={() => navigate("/offres")}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 rounded-xl px-7 py-4 font-semibold text-lg transition-colors"
                >
                  Voir les offres
                </button>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
                <span className="flex items-center gap-2"><Check size={16} className="text-green-600" /> Sans engagement</span>
                <span className="flex items-center gap-2"><Check size={16} className="text-green-600" /> Service local et humain</span>
                <span className="flex items-center gap-2"><Check size={16} className="text-green-600" /> Adapté aux petits budgets</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
              <img src={IMAGES.storefront} alt="Visibilité d'un commerce local" className="rounded-2xl shadow-xl w-full object-cover" />
              <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-white rounded-xl shadow-lg p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-green-50 text-green-600 flex items-center justify-center">
                  <Star size={20} className="fill-green-600" />
                </div>
                <div>
                  <p className="font-heading font-bold text-slate-900 leading-none">+ de clients</p>
                  <p className="text-xs text-slate-500 mt-1">grâce à une présence claire</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* POURQUOI C'EST UTILE */}
      <Section className="bg-white">
        <SectionHeader
          eyebrow="Pourquoi c'est utile"
          title="Une présence en ligne claire change tout pour un commerce local"
          subtitle="Quand vos clients vous trouvent facilement et vous font confiance, ils franchissent la porte."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                data-testid={`benefit-card-${i}`}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 hover:shadow-md transition-shadow"
              >
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-green-50 text-green-600 mb-4">
                  <Icon size={24} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed">{b.text}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* CE QUE NOUS FAISONS */}
      <Section className="bg-slate-50">
        <SectionHeader
          eyebrow="Ce que nous faisons"
          title="Tout ce qu'il faut pour être visible, en simple"
          subtitle="Des services concrets, expliqués sans jargon, pensés pour les commerçants pressés."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={fadeUp}
                data-testid={`service-card-${i}`}
                className="flex gap-4 bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-md transition-shadow"
              >
                <div className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-slate-900 text-green-400">
                  <Icon size={22} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* DIAGNOSTIC */}
      <Section id="diagnostic" className="bg-white">
        <SectionHeader
          eyebrow="Diagnostic visibilité"
          title="Découvrez votre potentiel d'amélioration"
          subtitle="Une analyse complète en 6 étapes : score de visibilité, potentiel d'amélioration et pack recommandé selon votre situation."
        />
        <DiagnosticWizard />
      </Section>

      {/* PACKS */}
      <Section id="offres" className="bg-slate-50">
        <SectionHeader
          eyebrow="Nos offres"
          title="Trois packs simples pour avancer à votre rythme"
          subtitle="Pas sûr du bon choix ? Le diagnostic gratuit vous recommande le pack le plus adapté à votre situation."
        />
        <PacksSection />
        <p className="text-center text-slate-500 mt-10 max-w-2xl mx-auto text-sm">{PRESTATIONS_NOTE}</p>
        <p className="text-center text-slate-500 mt-4">
          Besoin d'aide pour choisir ?{" "}
          <Link to="/diagnostic" className="text-green-600 font-semibold hover:text-green-700">Faites le diagnostic gratuit →</Link>
        </p>
      </Section>

      {/* COMMENT CA MARCHE */}
      <Section className="bg-white">
        <SectionHeader eyebrow="Comment ça marche" title="Simple, en 3 étapes" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} data-testid={`step-card-${i}`} className="relative text-center px-4">
                <div className="mx-auto inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-green-600 text-white mb-5">
                  <Icon size={28} />
                </div>
                <span className="absolute top-0 right-1/2 translate-x-16 font-heading text-5xl font-bold text-slate-100">{i + 1}</span>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.text}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* TEMOIGNAGES */}
      <Section className="bg-slate-900">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <p className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-3">Témoignages</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Ils ont gagné en visibilité</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={i} data-testid={`testimonial-${i}`} className="bg-slate-800 rounded-2xl p-7 border border-slate-700">
              <Quote size={28} className="text-green-400 mb-4" />
              <p className="text-slate-200 leading-relaxed mb-6">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-green-600 text-white flex items-center justify-center font-heading font-bold">
                  {t.name.charAt(0)}
                </div>
                <div>
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="text-sm text-slate-400">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* CONFIANCE */}
      <Section className="bg-white">
        <SectionHeader eyebrow="Pourquoi nous faire confiance" title="Un partenaire local, humain et concret" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {TRUST.map((t, i) => {
            const Icon = t.icon;
            return (
              <div key={i} data-testid={`trust-card-${i}`} className="flex items-start gap-4 rounded-2xl bg-slate-50 p-6">
                <div className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded-xl bg-green-600 text-white">
                  <Icon size={22} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-slate-900 mb-1">{t.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{t.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-slate-50">
        <SectionHeader eyebrow="FAQ" title="Vos questions, nos réponses" />
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {FAQ.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="bg-white rounded-xl border border-slate-100 px-6">
                <AccordionTrigger className="text-left font-heading font-semibold text-slate-900 hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-3">Contact</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Parlons de votre commerce</h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-8">
              Une question, un projet, envie d'en savoir plus ? Écrivez-nous, on vous recontacte rapidement.
            </p>
            <div className="space-y-3">
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-call-button" className="flex items-center gap-3 rounded-xl border border-slate-200 hover:border-green-600 p-4 transition-colors">
                <span className="h-11 w-11 rounded-xl bg-green-50 text-green-600 flex items-center justify-center"><Phone size={20} /></span>
                <span><span className="block text-sm text-slate-500">Appelez-nous</span><span className="font-semibold text-slate-900">{CONTACT.phone}</span></span>
              </a>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-button" className="flex items-center gap-3 rounded-xl border border-slate-200 hover:border-green-600 p-4 transition-colors">
                <span className="h-11 w-11 rounded-xl bg-green-50 text-green-600 flex items-center justify-center"><MessageCircle size={20} /></span>
                <span><span className="block text-sm text-slate-500">Écrivez sur WhatsApp</span><span className="font-semibold text-slate-900">Discuter maintenant</span></span>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-button" className="flex items-center gap-3 rounded-xl border border-slate-200 hover:border-green-600 p-4 transition-colors">
                <span className="h-11 w-11 rounded-xl bg-green-50 text-green-600 flex items-center justify-center"><Mail size={20} /></span>
                <span><span className="block text-sm text-slate-500">Par email</span><span className="font-semibold text-slate-900 break-all">{CONTACT.email}</span></span>
              </a>
              <p className="flex items-center gap-2 text-sm text-slate-500 pt-2"><MapPin size={16} className="text-green-600" /> {CONTACT.zoneText}</p>
            </div>
          </div>
          <ContactForm />
        </div>
      </Section>
    </div>
  );
}
